import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Wallet, TrendingUp, Clock, Euro, Users } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";

const fmtEUR = (n) => (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
const fmtEUR0 = (n) => (n == null ? "—" : (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const mesLabel = (m) => {
  const [y, mm] = (m || "").split("-");
  return mm ? `${MESES[parseInt(mm, 10) - 1]} ${y}` : m;
};

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <Card><CardContent className="flex items-center gap-3 p-4">
    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
    <div><p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-xl font-bold tabular-nums">{value}</p></div>
  </CardContent></Card>
);

export default function TesoreriaPage() {
  return (
    <div className="space-y-6" data-testid="tesoreria-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Wallet className="h-6 w-6 text-primary" /> Tesorería
        </h1>
        <p className="text-sm text-muted-foreground">
          Cashflow de reservas y costes de personal (nóminas + Seguridad Social).
        </p>
      </div>

      <Tabs defaultValue="cashflow">
        <TabsList data-testid="tes-tabs">
          <TabsTrigger value="cashflow" data-testid="tes-tab-cashflow"><TrendingUp className="mr-1.5 h-4 w-4" />Cashflow</TabsTrigger>
          <TabsTrigger value="personal" data-testid="tes-tab-personal"><Users className="mr-1.5 h-4 w-4" />Costes de personal</TabsTrigger>
        </TabsList>
        <TabsContent value="cashflow" className="mt-4"><CashflowPanel /></TabsContent>
        <TabsContent value="personal" className="mt-4"><CostesPersonalPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function CashflowPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["cashflow-aggregate"],
    queryFn: async () => (await api.get("/cashflow/aggregate")).data,
  });
  const meses = data?.meses || [];
  const tot = data?.totales || { pagado: 0, pendiente: 0 };
  const max = Math.max(1, ...meses.map((m) => Math.max(m.pagado, m.pendiente)));

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Pagado por mes (según fecha del pago) y pendiente por mes (imputado al mes de ejecución de la reserva).
      </p>
      {isLoading ? <Skeleton className="h-24 w-full" /> : (
        <div className="grid grid-cols-2 gap-3 sm:max-w-md">
          <Kpi icon={TrendingUp} label="Total pagado" value={fmtEUR(tot.pagado)} tone="bg-emerald-100 text-emerald-700" />
          <Kpi icon={Clock} label="Total pendiente" value={fmtEUR(tot.pendiente)} tone="bg-red-100 text-red-700" />
        </div>
      )}

      <Card><CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : meses.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Aún no hay pagos ni pendientes registrados.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-28">Mes</TableHead>
              <TableHead className="text-right">Pagado</TableHead>
              <TableHead className="text-right">Pendiente</TableHead>
              <TableHead>Comparativa</TableHead>
            </TableRow></TableHeader>
            <TableBody data-testid="tesoreria-table-body">
              {meses.map((m) => (
                <TableRow key={m.mes} data-testid={`cf-row-${m.mes}`}>
                  <TableCell className="font-medium capitalize">{mesLabel(m.mes)}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-700">{fmtEUR(m.pagado)}</TableCell>
                  <TableCell className="text-right tabular-nums text-red-700">{fmtEUR(m.pendiente)}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${(m.pagado / max) * 100}%` }} />
                      <div className="h-2 rounded-full bg-red-400" style={{ width: `${(m.pendiente / max) * 100}%` }} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <Card className={accent ? "border-amber-200" : ""}><CardContent className="p-4">
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`mt-1 font-heading text-2xl font-bold ${accent ? "text-amber-600" : ""}`}>{value}</p>
  </CardContent></Card>
);

function CostesPersonalPanel() {
  const q = useQuery({ queryKey: ["rrhh-nominas-costes"], queryFn: async () => (await api.get("/rrhh/nominas/costes")).data });
  if (q.isLoading) return <Skeleton className="h-72 w-full" />;
  const d = q.data || {};
  const t = d.totales || {};
  const mes = (d.por_mes || []).map((m) => ({ name: m.periodo_label || m.periodo, Bruto: m.bruto, "Coste empresa": m.coste_empresa }));
  const hasData = (d.por_mes || []).length > 0 || (d.por_empleado || []).length > 0;
  return (
    <div className="space-y-5" data-testid="tes-costes-personal">
      <p className="text-sm text-muted-foreground">
        Coste salarial total para la empresa (Bruto + Seguridad Social a cargo de la empresa). El total mensual se
        vuelca automáticamente en Gastos Fijos › Nóminas.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Bruto total" value={fmtEUR0(t.bruto)} />
        <StatCard label="Coste empresa total" value={fmtEUR0(t.coste_empresa)} accent />
        <StatCard label="Neto pagado" value={fmtEUR0(t.neto)} />
        <StatCard label="Nóminas" value={t.count ?? 0} />
      </div>

      {!hasData ? (
        <Card><CardContent className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid="tes-costes-empty">
          <Euro className="h-6 w-6" />Todavía no hay nóminas procesadas. Súbelas desde RRHH › Nóminas.
        </CardContent></Card>
      ) : (
        <>
          {mes.length > 0 && (
            <Card><CardContent className="p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Coste por mes</h3>
              <div className="h-64 w-full" data-testid="tes-costes-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mes} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={52} />
                    <Tooltip formatter={(v) => fmtEUR(v)} />
                    <Legend />
                    <Bar dataKey="Bruto" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Coste empresa" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>
          )}
          <Card><CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="p-3">Empleado</th><th className="p-3 text-right">Bruto</th><th className="p-3 text-right">Coste empresa</th><th className="p-3 text-right">Nóminas</th></tr>
              </thead>
              <tbody className="divide-y" data-testid="tes-costes-emp">
                {(d.por_empleado || []).map((e) => (
                  <tr key={e.user_id}>
                    <td className="p-3 font-medium">{e.full_name}</td>
                    <td className="p-3 text-right tabular-nums">{fmtEUR(e.bruto)}</td>
                    <td className="p-3 text-right tabular-nums">{fmtEUR(e.coste_empresa)}</td>
                    <td className="p-3 text-right tabular-nums">{e.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
